import { useEffect } from "react"
import { getTranslations } from "../../i18n"
import { useLocale } from "../../locale-context"

/**
 * Keyboard chrome for the media detail modals (ZPL-267): Escape closes,
 * ArrowLeft/ArrowRight navigate — except while the user is typing in an
 * edit field (inputs own their caret movement).
 */
export function useMediaModalKeys(handlers: {
	onClose: () => void
	onPrev?: () => void
	onNext?: () => void
}) {
	const { onClose, onPrev, onNext } = handlers
	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				onClose()
				return
			}
			const typing =
				e.target instanceof HTMLInputElement ||
				e.target instanceof HTMLTextAreaElement
			if (typing) return
			if (e.key === "ArrowLeft") onPrev?.()
			if (e.key === "ArrowRight") onNext?.()
		}
		window.addEventListener("keydown", handler)
		return () => window.removeEventListener("keydown", handler)
	}, [onClose, onPrev, onNext])
}

function Chevron({ direction }: { direction: "left" | "right" }) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			className="w-6 h-6"
			aria-hidden="true"
		>
			{direction === "left" ? (
				<path d="m15 6-6 6 6 6" />
			) : (
				<path d="m9 6 6 6-6 6" />
			)}
		</svg>
	)
}

/**
 * Prev/next zones overlaid on the modal's media area. Each zone is the outer
 * 15% of the media width — hovering anywhere in it tints the zone and lights
 * up its chevron. Render only when a handler exists — a single viewable item
 * shows no navigation at all.
 */
export function MediaNavChevrons({
	onPrev,
	onNext,
}: {
	onPrev?: () => void
	onNext?: () => void
}) {
	const { locale } = useLocale()
	const t = getTranslations(locale)
	if (!onPrev && !onNext) return null
	const zone =
		"group absolute inset-y-0 w-[15%] transition-colors hover:bg-black/10 focus-visible:outline-none focus-visible:bg-black/10"
	const pill =
		"absolute top-1/2 flex -translate-y-1/2 rounded-full bg-black/40 p-2 text-white transition-colors group-hover:bg-black/60 group-focus-visible:ring-2 group-focus-visible:ring-ring"
	return (
		<>
			{onPrev ? (
				<button
					type="button"
					onClick={onPrev}
					aria-label={t.mediaModal.prev}
					className={`${zone} left-0`}
				>
					<span className={`${pill} left-2`}>
						<Chevron direction="left" />
					</span>
				</button>
			) : null}
			{onNext ? (
				<button
					type="button"
					onClick={onNext}
					aria-label={t.mediaModal.next}
					className={`${zone} right-0`}
				>
					<span className={`${pill} right-2`}>
						<Chevron direction="right" />
					</span>
				</button>
			) : null}
		</>
	)
}
